import React, { useEffect, useMemo, useState } from 'react'
import { Box, MenuItem, TextField } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelColors, formatNumber } from './components/intelTheme'
import { intelApi } from './api'
import { ChartCard, Bars } from './components/IntelCharts'

const DIRECTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'FORWARD', label: 'Forward' },
  { value: 'RVP', label: 'Reverse' },
]

const GROUPINGS = [
  { value: 'SERVICE_TYPE', label: 'By Service Type' },
  { value: 'PARENT', label: 'By Parent Courier' },
  { value: 'CODE', label: 'By Courier Code' },
]

const pctBadge = (v, invert) => {
  if (v == null) return '—'
  // invert=true → lower is better (RTO%); else higher is better (delivery%).
  const good = invert ? v < 8 : v >= 90
  const warn = invert ? v < 12 : v >= 80
  const color = good ? 'green' : warn ? 'orange' : 'red'
  return <Badge color={color}>{v}%</Badge>
}

const verdictColor = (r) => {
  if (!r) return 'grey'
  if (r === 'Healthy') return 'green'
  if (r === 'Needs review') return 'red'
  if (r === 'Low volume') return 'grey'
  return 'orange'
}

export default function ServiceTypePerformance() {
  const [direction, setDirection] = useState('ALL')
  const [groupBy, setGroupBy] = useState('SERVICE_TYPE')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    setExpanded({})
    intelApi
      .serviceTypePerformance({ direction, groupBy })
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [direction, groupBy])

  const rows = useMemo(() => data?.rows || [], [data])
  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }))

  const volBars = useMemo(
    () => rows.filter((r) => r.volume).slice(0, 12).map((r) => ({ name: r.name || r.code, value: r.volume })),
    [rows]
  )
  const delBars = useMemo(
    () => rows.filter((r) => r.deliveryPct != null).slice(0, 12).map((r) => ({ name: r.name || r.code, value: r.deliveryPct })),
    [rows]
  )

  const firstHeader = groupBy === 'CODE' ? 'Account Code' : groupBy === 'PARENT' ? 'Parent Courier' : 'Service Type'

  const columns = [
    {
      key: 'name',
      header: firstHeader,
      render: (r) => {
        const has = r.children && r.children.length
        return (
          <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {has ? <Box component='span' sx={{ color: intelColors.muted, fontSize: 10 }}>{expanded[r.code] ? '▾' : '▸'}</Box> : null}
            <strong>{r.name || r.code}</strong>
          </Box>
        )
      },
    },
    { key: 'category', header: 'Category', render: (r) => (r.category ? <Badge color='blue'>{r.category}</Badge> : '—') },
    { key: 'direction', header: 'Direction', render: (r) => (r.direction ? <Badge color={r.direction === 'RVP' ? 'orange' : 'blue'}>{r.direction}</Badge> : '—') },
    { key: 'codes', header: '# Codes', render: (r) => formatNumber(r.codes) },
    { key: 'pickupPins', header: 'Pickup PINs', render: (r) => (r.pickupPins != null ? formatNumber(r.pickupPins) : '—') },
    { key: 'deliveryPins', header: 'Delivery PINs', render: (r) => (r.deliveryPins != null ? formatNumber(r.deliveryPins) : '—') },
    {
      key: 'deliveryPct',
      header: 'Delivery %',
      info: 'Volume-weighted across the courier codes, from orders (trailing 30d).',
      render: (r) => pctBadge(r.deliveryPct, false),
    },
    { key: 'rtoPct', header: 'RTO %', info: 'RTO-family orders / shipped, 30d.', render: (r) => pctBadge(r.rtoPct, true) },
    { key: 'transitDays', header: 'Transit (d)', render: (r) => (r.transitDays != null ? r.transitDays : '—') },
    { key: 'volume', header: 'Volume (30d)', render: (r) => formatNumber(r.volume) },
    { key: 'recommendation', header: 'Recommendation', render: (r) => (r.recommendation ? <Badge color={verdictColor(r.recommendation)}>{r.recommendation}</Badge> : '—') },
  ]

  // Custom row render: parent row (clickable when it has children) + indented child code rows when expanded.
  const renderRow = (r, idx) => {
    const key = r.code || idx
    const has = r.children && r.children.length
    const open = !!expanded[key]
    return (
      <React.Fragment key={key}>
        <tr onClick={() => has && toggle(key)} style={{ cursor: has ? 'pointer' : 'default' }}>
          {columns.map((c) => (
            <td key={c.key} style={c.cellStyle || {}}>{c.render ? c.render(r, idx) : r[c.key]}</td>
          ))}
        </tr>
        {open &&
          r.children.map((ch, ci) => (
            <tr key={`${key}-${ci}`} style={{ background: '#fafafa' }}>
              {columns.map((c, cidx) => (
                <td key={c.key} style={{ ...(c.cellStyle || {}), paddingLeft: cidx === 0 ? 28 : undefined, color: '#555' }}>
                  {c.render ? c.render(ch, ci) : ch[c.key]}
                </td>
              ))}
            </tr>
          ))}
      </React.Fragment>
    )
  }

  return (
    <IntelLayout title='Service Types — Performance'>
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${intelColors.border}`,
          borderRadius: '8px',
          p: '14px 18px',
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <TextField select label='Group By' value={groupBy} onChange={(e) => setGroupBy(e.target.value)} size='small' sx={{ minWidth: 180 }}>
          {GROUPINGS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label='Direction' value={direction} onChange={(e) => setDirection(e.target.value)} size='small' sx={{ minWidth: 160 }}>
          {DIRECTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        {groupBy !== 'CODE' && <Box sx={{ fontSize: 11, color: intelColors.muted, pb: '6px' }}>Click a row to expand its courier codes.</Box>}
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      {(volBars.length > 0 || delBars.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', mb: 2 }}>
          <ChartCard loading={loading} title='Volume (30d)'>
            <Bars data={volBars} color='#1565c0' height={250} />
          </ChartCard>
          <ChartCard loading={loading} title='Delivery %'>
            <Bars data={delBars} color='#2e7d32' height={250} />
          </ChartCard>
        </Box>
      )}

      <IntelTable
        title={`Performance & Serviceability — ${GROUPINGS.find((g) => g.value === groupBy)?.label || ''}`}
        action={<Badge color='grey'>{`${data?.totalCount != null ? data.totalCount : rows.length} rows · ${data?.windowDays || 30}d`}</Badge>}
        loading={loading}
        columns={columns}
        rows={rows}
        renderRow={renderRow}
        pageSize={50}
        emptyMsg='No data'
      />
    </IntelLayout>
  )
}
